import set from "@rill/set";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkOrganizationSectionPermission } from "../../../middleware/ensure-coach-permissions";
import SettingsNav from "../../../parts/settings-nav";
import { getUserAccess } from "../../../utils/organization-helpers";
import {
  isPerformActiveCoach,
  isPullActiveCoach,
} from "../../../utils/user-helpers";
import { SettingsBackHandler } from "../../coaching-portal/settings/components/SettingsBackHandler";

function YourProductsPage({ locals }) {
  const {
    activeUser,
    organizationOwner,
    organizationPermissions,
    organizationRoles,
    activeUserToRequest,
  } = locals;

  const { t, i18n } = useTranslation(["dashboard", "coaching-portal"]);

  // Determine product access
  const userAccess = getUserAccess(
    activeUser,
    organizationOwner,
    organizationPermissions,
    organizationRoles
  );

  const isPullActive = isPullActiveCoach(activeUserToRequest);
  const isPerformActive = isPerformActiveCoach(activeUserToRequest);
  const isPerformEnterprise =
    activeUserToRequest.performSettings?.enterprisePricing;

  const locale = (i18n.language || "en").split("-")[0];

  const PRODUCTS = useMemo(() => {
    const roleLower = activeUser.referAsClients
      ? t("coaching-portal:athletes.roleClientLower")
      : t("coaching-portal:athletes.roleAthleteLower");

    return [
      {
        id: "push",
        name: "PUSH",
        icon: "PU",
        description: t("yourProducts.products.push.description"),
        isActive: userAccess.userCanAccessToPush,
        billingAmount: t("yourProducts.products.push.billingAmount"),
        billingUnit: t("yourProducts.products.push.billingUnit"),
        nextInvoice: t("yourProducts.products.push.nextInvoiceNote"),
        manageUrl: "/coaching-portal/support/contact",
        manageText: t("yourProducts.contactSupport"),
        requestAccessUrl: "https://form.typeform.com/to/jOPku6ra",
      },
      {
        id: "pull",
        name: "PULL",
        icon: "PL",
        description: t("yourProducts.products.pull.description"),
        isActive: isPullActive,
        billingAmount: t("yourProducts.products.pull.billingAmount", {
          price: t("yourProducts.products.pull.billingAmountPrice"),
          role: roleLower,
        }),
        billingUnit: t("yourProducts.products.pull.billingUnit"),
        nextInvoice: moment()
          .endOf("month")
          .locale(locale)
          .format("LL"),
        manageUrl: "/coaching-portal/settings/coach-billing",
        manageText: t("yourProducts.viewInvoices"),
        requestAccessUrl: "https://mystrengthbook.com/pull/",
      },
      {
        id: "press",
        name: "PRESS",
        icon: "PR",
        description: t("yourProducts.products.press.description"),
        isActive: userAccess.userCanAccessToPress,
        billingAmount: t("yourProducts.products.press.billingAmount"),
        billingUnit: t("yourProducts.products.press.billingUnit"),
        nextInvoice: t("yourProducts.products.press.nextInvoiceNote"),
        manageUrl: "/coaching-portal/support/contact",
        manageText: t("yourProducts.contactSupport"),
        requestAccessUrl: "https://form.typeform.com/to/jOPku6ra",
      },
      {
        id: "perform",
        name: "PERFORM",
        icon: "PE",
        description: t("yourProducts.products.perform.description"),
        isActive: isPerformActive,
        billingAmount: isPerformEnterprise
          ? t("yourProducts.products.perform.billingAmountEnterprise")
          : t("yourProducts.products.perform.billingAmountStandard"),
        billingUnit: isPerformEnterprise
          ? t("yourProducts.products.perform.billingUnitEnterprise")
          : t("yourProducts.products.perform.billingUnitStandard"),
        nextInvoice: moment()
          .add(1, "month")
          .startOf("month")
          .locale(locale)
          .format("LL"),
        manageUrl: "/coaching-portal/settings/perform-invoice",
        manageText: t("yourProducts.viewInvoices"),
        requestAccessUrl: "https://form.typeform.com/to/jOPku6ra",
      },
    ];
  }, [
    t,
    locale,
    activeUser.referAsClients,
    userAccess.userCanAccessToPush,
    userAccess.userCanAccessToPress,
    isPullActive,
    isPerformActive,
    isPerformEnterprise,
  ]);

  const title = t("settingsNav.pages.yourProducts");

  return (
    <main className="settings">
      <SettingsNav classNames="mobile-hidden" />

      <section className="your-products">
        <h3 className="force-mobile-hidden">{title}</h3>
        <SettingsBackHandler title={title} />
        <p className="p2 mt-4">{t("yourProducts.intro")}</p>

        <section role="table-wrapper" data-borders-inside>
          <table role="report-table" className="products-table">
            <thead role="table-header">
              <tr>
                <th>{t("yourProducts.table.product")}</th>
                <th>{t("yourProducts.table.status")}</th>
                <th>{t("yourProducts.table.billingAmount")}</th>
                <th>{t("yourProducts.table.nextInvoice")}</th>
                <th>{t("yourProducts.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {PRODUCTS.map((product) => (
                <tr key={product.id}>
                  <td data-label={t("yourProducts.table.product")}>
                    <div className="product-info">
                      <div className={`product-icon ${product.id}`}>
                        {product.icon}
                      </div>
                      <div className="product-details">
                        <div className="product-name">{product.name}</div>
                        <div className="product-description">
                          {product.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td data-label={t("yourProducts.table.status")}>
                    <span
                      className={`status-badge ${
                        product.isActive ? "active" : "inactive"
                      }`}
                    >
                      {product.isActive
                        ? t("settingsNav.productActive")
                        : t("settingsNav.productInactive")}
                    </span>
                  </td>
                  <td data-label={t("yourProducts.table.billingAmount")}>
                    {product.isActive ? (
                      <>
                        {product.billingAmount}
                        <p className="p3">{product.billingUnit}</p>
                      </>
                    ) : (
                      t("yourProducts.emptyCell")
                    )}
                  </td>
                  <td data-label={t("yourProducts.table.nextInvoice")}>
                    {product.isActive
                      ? product.nextInvoice
                      : t("yourProducts.emptyCell")}
                  </td>
                  <td data-label={t("yourProducts.table.actions")}>
                    {product.isActive ? (
                      <a
                        href={product.manageUrl}
                        role="button"
                        data-button-type="outline"
                      >
                        {product.manageText || t("yourProducts.manage")}
                      </a>
                    ) : (
                      <a
                        href={product.requestAccessUrl}
                        role="button"
                        data-button-type="primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("yourProducts.requestAccess")}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <aside className="help-section">
          <div>
            <h4>{t("yourProducts.helpTitle")}</h4>
            <p>{t("yourProducts.helpBody")}</p>
          </div>

          <a
            href="/coaching-portal/support/contact"
            role="button"
            className="text-black bg-white"
          >
            {t("yourProducts.contactSupport")}
          </a>
        </aside>
      </section>
    </main>
  );
}

const YourProducts = (app) => {
  app.get(
    "/your-products",
    set("heading", "Your Products"),
    (ctx, next) =>
      checkOrganizationSectionPermission(
        ctx,
        next,
        "settings",
        "/coaching-portal/settings",
        ["Owner", "Admin", "Member"]
      ),
    ({ res, locals }) => {
      res.body = <YourProductsPage locals={locals} />;
    }
  );
};

export default YourProducts;
